import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import { useQuery } from 'react-query'
import { TruckloadSMContainer, TruckloadSMInventory } from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { Barcode } from 'src/components/Barcode'

const useStyles = makeStyles(() => ({
  table: {
    width: '100%',
    margin: '0',
    padding: '0',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '.25em',
    fontSize: '.85em',
    fontWeight: 'bold',
  },
  tr: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
    fontSize: '.8em',
  },
  td: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
  },
  strong: {
    fontWeight: 'bold',
  },
  label: {
    maxWidth: '90%',
    margin: 'auto',
    border: '1px solid gray',
    borderRadius: '1rem',
    padding: '0.5rem',
  },
  tspaced: {
    marginTop: '1rem',
  },
  bspaced: {
    marginBottom: '1rem',
  },
}))

interface InventorySMProps {
  inventory: TruckloadSMInventory[]
}

const InventorySM = (props: InventorySMProps) => {
  const { inventory } = props
  const classes = useStyles()

  return (
    <>
      <table className={classes.table} aria-label="units table">
        <thead>
          <tr>
            <th className={classes.th}>Sku</th>
            <th className={classes.th}>Product</th>
            <th className={classes.th}>Number of Units</th>
            <th className={classes.th}>Total in Shipment</th>
            <th className={classes.th}>Ordered</th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((row) => (
            <tr key={row.sku?.guid} className={`card-break ${classes.tr}`}>
              <td className={classes.td} align="right">
                {row.sku?.sku}
              </td>
              <td className={classes.td} align="right">
                {row.sku?.product?.name}
              </td>
              <td className={classes.td} align="right">
                {row.numUnits}
              </td>
              <td className={classes.td} align="right">
                {row.totalUnits}
              </td>
              <td className={classes.td} align="right">
                {row.numOrdered}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

interface ContainerSMProps {
  container: TruckloadSMContainer
}

const ContainerSM = (props: ContainerSMProps) => {
  const { container } = props
  const classes = useStyles()

  return (
    <div className={classes.tspaced}>
      <Typography variant="h5">Name: {container.name}</Typography>
      <Typography variant="h5">SquID: {container.guid}</Typography>
      <InventorySM inventory={container.inventory || []} />
    </div>
  )
}

interface ShipmentSMProps {
  guid: string
  onReadyToPrint?: () => void
}

export const PackingSlip = (props: ShipmentSMProps) => {
  const { guid, onReadyToPrint } = props
  const barcode = { barcode: guid, type: 'datamatrix', displayName: '' }
  const classes = useStyles()
  const { isLoading, data: shipment } = useQuery(['packing-slip'], () =>
    skuit.client.freight.shipments.packingSlip(guid)
  )

  useEffect(() => {
    if (!isLoading) {
      onReadyToPrint?.()
    }
  }, [isLoading, onReadyToPrint])

  if (isLoading) {
    return <div>Packing Slip not ready to print</div>
  }

  return (
    <div className={`always-page-break   ${classes.bspaced}`}>
      <div className="card-break">
        <Box px={10} pt={20}>
          <Grid container justify="space-between" alignItems="center" spacing={4}>
            <Grid item xs={9}>
              <Box>
                <Typography variant="h3">Shipment</Typography>
              </Box>
              <Box>
                <Typography variant="h5">SquID: {shipment?.guid}</Typography>
              </Box>
              <Box>
                <Typography variant="h5">Tracking #: {shipment?.trackingNumber || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="h5">
                  {shipment?.order
                    ? `Order #${shipment.order.number || '-'}`
                    : 'No order on shipment'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Barcode barcode={barcode} />
            </Grid>
          </Grid>
        </Box>
      </div>

      <Grid container>
        <Grid item xs={6}>
          <div className={`${classes.bspaced} ${classes.tspaced}`}>
            <Typography variant="h3">Address</Typography>
            <>
              <Typography>{shipment.address.address1}</Typography>
              {shipment.address.address2 && <Typography>{shipment.address.address2}</Typography>}
              <Typography>
                {shipment.address.city}, {shipment.address.state} {shipment.address.postalCode}
              </Typography>
            </>
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className={`${classes.bspaced} ${classes.tspaced}`}>
            <Typography variant="h3">Summary</Typography>
            <Typography>{shipment.totalUnits} Units on {shipment.numContainers} Pallets</Typography>
          </div>
        </Grid>
      </Grid>

      <div className={`${classes.bspaced} ${classes.tspaced}`}>
        <Typography variant="h3">Inventory by Pallet</Typography>
      </div>

      {!!shipment?.containers?.length &&
        shipment.containers.map((container: TruckloadSMContainer) => (
          <ContainerSM container={container} key={container.guid} />
        ))}

      {!!shipment?.inventory?.length && (
        <>
          <Typography variant="h5">No Pallet</Typography>
          <InventorySM inventory={shipment.inventory || []} />
        </>
      )}
    </div>
  )
}
